import { Grid, Typography, Box, ButtonBase, Radio } from '@mui/material'
import React from 'react'
import { useStrings } from '../../assets/localization/strings'
import { CampaignGender } from '../../models/GeneralTypes'
import { Controller, useFormContext } from 'react-hook-form'
import rootStore from '../../stores/rootStore'
import AllgendersIcon from '../../assets/icons/allgenders_icon.svg'
import MaleIcon from '../../assets/icons/male_icon.svg'
import FemaleIcon from '../../assets/icons/female_icon.svg'
import DiverseIcon from '../../assets/icons/diverse_icon.svg'
import GermanFlag from '../../assets/icons/germany_icon.png'
import AustriaFlag from '../../assets/icons/austria_icon.png'
import SwitzerlandFlag from '../../assets/icons/swiss_icon.png'
import FranceFlag from '../../assets/icons/france_icon.png'
import EnglishFlag from '../../assets/icons/england_icon.png'
import PolandFlag from '../../assets/icons/poland_icon.png'
import NetherlandsFlag from '../../assets/icons/netherland_icon.png' 
import { ReactComponent as CheckedIcon } from '../../assets/icons/checked_icon.svg'

interface IOption<T = string> {
  value: T
  label: string
  iconSrc?: string
}

const CampaignAudience = () => {
  const genders = useStrings()
  const languages = useStrings()
  const strings = useStrings()

  const genderOptions: IOption<CampaignGender>[] = [
    { value: CampaignGender.ANY, label: genders.campaign_creation_gender_any, iconSrc: AllgendersIcon },
    { value: CampaignGender.MALE, label: genders.campaign_creation_gender_male, iconSrc: MaleIcon },
    { value: CampaignGender.FEMALE, label: genders.campaign_creation_gender_female, iconSrc: FemaleIcon },
    { value: CampaignGender.DIVERSE, label: genders.campaign_creation_gender_diverse, iconSrc: DiverseIcon }
  ]

  const languageOptions: IOption<'de' | 'en' | 'at' | 'ch' | 'fr' | 'pl' | 'nl'>[] = [
    { value: 'de', label: languages.campaign_creation_language_german, iconSrc: GermanFlag },
    { value: 'en', label: languages.campaign_creation_language_english, iconSrc: EnglishFlag },
    { value: 'at', label: languages.campaign_creation_language_austrian, iconSrc: AustriaFlag },
    { value: 'ch', label: languages.campaign_creation_language_swiss, iconSrc: SwitzerlandFlag },
    { value: 'fr', label: languages.campaign_creation_language_french, iconSrc: FranceFlag },
    { value: 'pl', label: languages.campaign_creation_language_polish, iconSrc: PolandFlag },
    { value: 'nl', label: languages.campaign_creation_language_dutch, iconSrc: NetherlandsFlag },
  ]

  const englishLanguageOptions: IOption<'en'>[] = [{ value: 'en', label: languages.campaign_creation_language_english, iconSrc: EnglishFlag }]

  const isEnglishCountry = ['gb'].includes(rootStore.brandStore.selectedBrand?.brandCountry || 'de')

  const { control } = useFormContext()

  const renderOptions = <T,>(fieldName: string, options: IOption<T>[], selectedValue: T, onChange: (val: T) => void) => (
    <Box display='flex' flexWrap='wrap' justifyContent={'flex-start'} gap={2} mt={2} style={{ flexDirection: 'row' }}>
      {options.map(option => {
        const isSelected = selectedValue === option.value

        return (
          <ButtonBase
            key={option.value as string}
            onClick={() => onChange(option.value)}
            sx={{
              width: '279px',
              height: '75px',
              borderRadius: 2,
              border: `1px solid ${isSelected ? '#AB95FF' : '#ddd'}`,
              backgroundColor: isSelected ? '#AB95FF1A' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease-in-out'
            }}>
            <Radio checked={isSelected} checkedIcon={<CheckedIcon />} sx={{ position: 'absolute', right: '0px', top: '0px' }} value={option.value} />
            <Box display='flex' alignItems='center' gap={1} padding={'12px'} style={{ flex: 1 }}>
              {option.iconSrc && (
                <div
                  style={{
                    padding: '0px',
                    backgroundColor: isSelected ? '#AB95FF' : '#F1F3F4',
                    borderRadius: '999px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  <img src={option.iconSrc} alt={option.label} style={{ width: 38, height: 38, objectFit: 'cover' }} />
                </div>
              )}
              <Typography fontSize={'18px'} fontWeight={500} style={{ textAlign: 'center' }}>
                {option.label}
              </Typography>
            </Box>
          </ButtonBase>
        )
      })}
    </Box>
  )

  return (
    <Grid container spacing={2}>
      {/* Gender Selection */}
      <Grid item xs={12} mt={'8px'}>
        <Typography mb={1} variant='h5' sx={{ fontFamily: 'Bricolage Grotesque', fontWeight: 500, fontSize: '18px' }}>
          {strings.campaign_creation_gender_label}
        </Typography>

        <Controller control={control} name='gender' render={({ field }) => renderOptions<CampaignGender>(field.name, genderOptions, field.value, field.onChange)} />
      </Grid>

      {/* Language Selection */}
      <Grid item xs={12} mt={'12px'}>
        <Typography variant='h5' mb={1} sx={{ fontFamily: 'Bricolage Grotesque', fontWeight: 500, fontSize: '18px' }}>
          {strings.campaign_creation_language_label}
        </Typography>

        <Controller
          control={control}
          name='language'
          render={({ field }) => renderOptions<string>(field.name, isEnglishCountry ? englishLanguageOptions : languageOptions, field.value, field.onChange)}
        />
      </Grid>
    </Grid>
  )
}

export default CampaignAudience
